import { useAppSelector } from './redux';
import { selectHasPermission, selectHasRole, selectUser } from '../store/slices/authSlice';
import { PermissionKey, RoleName } from '../types/auth';

/**
 * Hook to check if user has a specific permission
 */
export const usePermission = (permission: PermissionKey): boolean => {
  return useAppSelector(state => selectHasPermission(state, permission));
};

/**
 * Hook to check if user has any of the specified permissions
 */
export const usePermissions = (permissions: PermissionKey[]): boolean => {
  return useAppSelector(state => 
    permissions.some(permission => selectHasPermission(state, permission))
  );
};

/**
 * Hook to check if user has all of the specified permissions
 */
export const useAllPermissions = (permissions: PermissionKey[]): boolean => {
  return useAppSelector(state => 
    permissions.every(permission => selectHasPermission(state, permission))
  );
};

/**
 * Hook to check if user has a specific role
 */
export const useRole = (role: RoleName): boolean => {
  return useAppSelector(state => selectHasRole(state, role));
};

/**
 * Hook to check if user has any of the specified roles
 */
export const useRoles = (roles: RoleName[]): boolean => {
  return useAppSelector(state => 
    roles.some(role => selectHasRole(state, role))
  );
};

/**
 * Hook to check if user has all of the specified roles
 */
export const useAllRoles = (roles: RoleName[]): boolean => {
  return useAppSelector(state => 
    roles.every(role => selectHasRole(state, role))
  );
};

/**
 * Hook to check if user is admin (Admin or SuperAdmin)
 */
export const useIsAdmin = (): boolean => {
  return useAppSelector(state => 
    selectHasRole(state, 'Admin') || selectHasRole(state, 'SuperAdmin')
  );
};

/**
 * Hook to check if user is SuperAdmin
 */
export const useIsSuperAdmin = (): boolean => {
  return useAppSelector(state => selectHasRole(state, 'SuperAdmin'));
};

/**
 * Hook to get user's permissions as a set for quick lookups
 */
export const useUserPermissions = (): Set<string> => {
  const user = useAppSelector(selectUser);
  return new Set(user?.permissions?.map(p => p.name) || []);
};

/**
 * Hook to get user's roles as a set for quick lookups
 */
export const useUserRoles = (): Set<string> => {
  const user = useAppSelector(selectUser);
  return new Set(user?.roles?.map(r => r.name) || []);
};

/**
 * Hook to check multiple permission conditions
 */
export const usePermissionCheck = (config: {
  permission?: PermissionKey;
  permissions?: PermissionKey[];
  requireAllPermissions?: PermissionKey[];
  role?: RoleName;
  roles?: RoleName[];
  requireAllRoles?: RoleName[];
  requireAdmin?: boolean;
  requireSuperAdmin?: boolean;
}): boolean => {
  const {
    permission,
    permissions,
    requireAllPermissions,
    role,
    roles,
    requireAllRoles,
    requireAdmin = false,
    requireSuperAdmin = false,
  } = config;

  const hasPermission = useAppSelector(state => 
    permission ? selectHasPermission(state, permission) : true
  );
  
  const hasAnyPermission = useAppSelector(state => {
    if (!permissions || permissions.length === 0) return true;
    return permissions.some(p => selectHasPermission(state, p));
  });
  
  const hasAllPermissions = useAppSelector(state => {
    if (!requireAllPermissions || requireAllPermissions.length === 0) return true;
    return requireAllPermissions.every(p => selectHasPermission(state, p));
  });
  
  const hasRole = useAppSelector(state => 
    role ? selectHasRole(state, role) : true
  );
  
  const hasAnyRole = useAppSelector(state => {
    if (!roles || roles.length === 0) return true;
    return roles.some(r => selectHasRole(state, r));
  });
  
  const hasAllRoles = useAppSelector(state => {
    if (!requireAllRoles || requireAllRoles.length === 0) return true;
    return requireAllRoles.every(r => selectHasRole(state, r));
  });
  
  const isAdmin = useAppSelector(state => 
    requireAdmin ? selectHasRole(state, 'Admin') || selectHasRole(state, 'SuperAdmin') : true
  );
  
  const isSuperAdmin = useAppSelector(state => 
    requireSuperAdmin ? selectHasRole(state, 'SuperAdmin') : true
  );

  return hasPermission && 
         hasAnyPermission && 
         hasAllPermissions && 
         hasRole && 
         hasAnyRole && 
         hasAllRoles && 
         isAdmin && 
         isSuperAdmin;
};
